type SubStringList = { text: string; class?: string };
type HighlightPos = { start: number; length: number };

export function useSuggestionHelper() {
  const highlightClass = 'v-list-item__mask';

  /**
   * Safely normalize a string. Returns empty string if input is not valid.
   */
  const normalizeString = (s?: string): string =>
    typeof s === 'string' ? s : '';

  /**
   * Filter autocomplete items based on query.
   * - Org members: match can be in display_name or email.
   * - Others: only email is checked.
   */
  function filterItems(
    itemText: string,
    queryText: string,
    item?: Record<string, unknown>
  ): boolean {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const v = (item as any)?.props?.value;
    if ((v?.header && v?.header?.hasEmailMatches) || v?.type === 'divider')
      return true;

    const query =
      typeof queryText === 'string' ? queryText.trim().toLocaleLowerCase() : '';
    if (!query) return true;
    const words = query.split(/\s+/).filter(Boolean);

    const emailLc = (itemText ?? '').toLocaleLowerCase();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const raw = (item as any)?.raw ?? {};
    const subj = (raw?.subject_type ?? '') as string;
    const displayLc = (raw?.display_name ?? '').toLocaleLowerCase();

    const isOrgMember = subj === 'user' || subj === 'service_user';

    if (isOrgMember) {
      return words.every((w) => emailLc.includes(w) || displayLc.includes(w));
    }
    return words.every((w) => emailLc.includes(w));
  }

  /**
   * Compute the start/length of all matches of query inside text.
   * Overlapping matches are merged into a single segment.
   */
  function extractHighlightPositions(
    text: string,
    query: string
  ): HighlightPos[] {
    if (!text || !query) return [];
    const tlc = text.toLocaleLowerCase();
    const words = query.toLocaleLowerCase().split(/\s+/).filter(Boolean);
    const hits: HighlightPos[] = [];

    for (const w of words) {
      let from = 0;

      while (true) {
        const idx = tlc.indexOf(w, from);
        if (idx === -1) break;
        hits.push({ start: idx, length: w.length });
        from = idx + w.length;
      }
    }

    hits.sort((a, b) => a.start - b.start || a.length - b.length);

    const merged: HighlightPos[] = [];
    for (const p of hits) {
      const last = merged[merged.length - 1];
      if (!last || p.start > last.start + last.length) {
        merged.push({ ...p });
      } else {
        const end = Math.max(last.start + last.length, p.start + p.length);
        last.length = end - last.start;
      }
    }
    return merged;
  }

  /**
   * Split text into segments with highlight class applied to matches.
   */
  function splitTextWithHighlights(
    text: string,
    query: string
  ): SubStringList[] {
    const positions = extractHighlightPositions(text, query);
    if (!positions.length) return text ? [{ text }] : [];

    const out: SubStringList[] = [];
    let pos = 0;

    for (const { start, length } of positions) {
      if (pos < start) out.push({ text: text.substring(pos, start) });
      out.push({
        text: text.substring(start, start + length),
        class: highlightClass
      });
      pos = start + length;
    }

    if (pos < text.length) out.push({ text: text.substring(pos) });
    return out;
  }

  /**
   * Parse email field into text segments with highlights.
   */
  function parseEmail(itemText: string, query: string): SubStringList[] {
    return splitTextWithHighlights(normalizeString(itemText), query);
  }

  /**
   * Parse display_name field into text segments with highlights.
   */
  function parseDisplayName(
    name: string | undefined,
    query: string
  ): SubStringList[] {
    return splitTextWithHighlights(normalizeString(name), query);
  }

  return { filterItems, parseEmail, parseDisplayName };
}
